import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Eyebrow,
  Field,
  Input,
  Select,
  Spinner,
  formatDate,
  money,
  useConfirm,
  usePrompt,
  type ConfirmRequest,
} from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Drawer, StatusBadge } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';
import { Icon, ICONS } from '../components/Icon.js';
import { ExperienceEditor } from '../components/ExperienceEditor.js';

/** One tenant: onboarding, contracts, domains, billing and lifecycle (docs/07). */

interface OnboardingStep {
  key: string;
  label: string;
  done: boolean;
  auto: boolean;
}

interface Contract {
  id: string;
  type: string;
  commissionBps: number | null;
  monthlyFeeCents: number | null;
  currency: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  notes: string | null;
}

interface Domain {
  id: string;
  domain: string;
  type: 'SUBDOMAIN' | 'CUSTOM';
  verifiedAt: string | null;
}

interface Invoice {
  id: string;
  number: string;
  periodStart: string;
  totalCents: number;
  currency: string;
  status: string;
}

interface TenantDetailData {
  id: string;
  name: string;
  slug: string;
  status: string;
  currency: string;
  timezone: string;
  previewUrl: string | null;
  previewLinks: { customer: string; admin: string };
  branding: { appName: string; colorPrimary: string; logoUrl: string | null } | null;
  domains: Domain[];
  contracts: Contract[];
  invoices: Invoice[];
  users: Array<{ id: string; name: string; email: string; role: string; active: boolean }>;
  onboarding: { steps: OnboardingStep[]; canGoLive: boolean; outstanding: string[] };
  last30Days: { bookings: number; gmvCents: number; commissionCents: number };
}

export function TenantDetail() {
  const t = useT();
  const { id = '' } = useParams();
  const tenant = useResource<TenantDetailData>(`/platform/tenants/${id}`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newContract, setNewContract] = useState(false);
  const [temporary, setTemporary] = useState<
    { name: string; temporaryPassword: string; expiresAt: string } | null
  >(null);
  const [newDomain, setNewDomain] = useState('');
  const { ask, dialog } = useConfirm();
  const { ask: askReason, dialog: reasonDialog } = usePrompt();

  async function act(path: string, payload?: unknown, confirm?: ConfirmRequest) {
    if (confirm && !(await ask(confirm))) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.post(`/platform/tenants/${id}/${path}`, payload ?? {});
      await tenant.reload();
      setNotice('Done.');
    } catch (err) {
      setError(errorMessage(err, 'That action did not complete.'));
    } finally {
      setBusy(false);
    }
  }

  if (tenant.loading) {
    return (
      <Page title="Tenant">
        <div className="grid place-items-center py-16">
          <Spinner label="Loading" />
        </div>
      </Page>
    );
  }
  if (!tenant.data) {
    return (
      <Page title="Tenant">
        <p style={{ color: 'var(--halt)' }}>{tenant.error}</p>
      </Page>
    );
  }

  const data = tenant.data;
  const currentContract = data.contracts.find((contract) => contract.effectiveTo === null);

  return (
    <Page
      title={data.name}
      description={`${data.slug} · ${data.currency} · ${data.timezone}`}
      actions={
        <div className="flex items-center gap-2">
          <StatusBadge status={data.status} />
          {data.status === 'ONBOARDING' && (
            <Button
              loading={busy}
              disabled={!data.onboarding.canGoLive}
              onClick={() =>
                act('go-live', {}, {
                  title: `Make ${data.name} public?`,
                  body: 'Their booking site becomes reachable to passengers immediately.',
                  confirmLabel: 'Go live',
                  destructive: false,
                })
              }
            >
              Go live
            </Button>
          )}
          {data.status === 'ACTIVE' && (
            <Button
              variant="danger"
              loading={busy}
              onClick={() => {
                void (async () => {
                  const reason = await askReason({
                    title: `Suspend ${data.name}?`,
                    body: 'Their booking site goes offline and no new bookings can be made. Existing tickets stay valid.',
                    label: 'Reason',
                    placeholder: 'Why this operator is being suspended',
                    confirmLabel: 'Suspend',
                  });
                  if (reason) await act('suspend', { reason });
                })();
              }}
            >
              Suspend
            </Button>
          )}
          {data.status === 'SUSPENDED' && (
            <Button loading={busy} onClick={() => act('reactivate')}>
              Reactivate
            </Button>
          )}
        </div>
      }
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}
      {error && (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      {/*
        * Both of this operator's addresses, opened rather than described.
        *
        * FIRST thing in the page, not buried at the foot of the onboarding checklist.
        * Opening the site is what this screen is used for most often, and a link that
        * needs a scroll to reach is a link nobody uses. A row rather than a card: two
        * addresses do not need a container, and one costs the height that put them
        * below the fold in the first place.
        *
        * Built server-side with the same resolver the API uses for outbound links, so
        * what opens here is what their passengers and staff get — a verified custom
        * domain once they have one, not a subdomain nobody uses any more.
        */}
      {data.previewLinks && (
        <div className="mb-5 flex flex-wrap gap-2">
          {[
            { label: t('preview.customer'), href: data.previewLinks.customer, icon: ICONS.operators },
            { label: t('preview.admin'), href: data.previewLinks.admin, icon: ICONS.staff },
          ].map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              title={t('preview.title')}
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg border px-3 py-2 transition-colors hover:bg-[var(--ink-800)] sm:flex-none"
              style={{ borderColor: 'var(--hairline-strong)' }}
            >
              <Icon d={link.icon} className="h-4 w-4 shrink-0" />
              <span className="min-w-0">
                <span className="block text-[0.8125rem] font-medium leading-tight">{link.label}</span>
                <span
                  className="font-mono block truncate text-[0.6875rem] leading-tight"
                  style={{ color: 'var(--chalk-faint)' }}
                >
                  {link.href.replace(/^https?:\/\//, '')}
                </span>
              </span>
              <Icon d={ICONS.external} className="h-3.5 w-3.5 shrink-0" />
            </a>
          ))}
          {/* The tokenised pre-live link, which only exists while a tenant is onboarding
              and is the only way to see their site before it is public. */}
          {data.previewUrl && (
            <a
              href={data.previewUrl}
              target="_blank"
              rel="noreferrer"
              className="font-mono flex items-center rounded-lg border border-dashed px-3 py-2 text-[0.6875rem] transition-colors hover:bg-[var(--ink-800)]"
              style={{ borderColor: 'var(--hairline-strong)', color: 'var(--chalk-faint)' }}
            >
              {data.previewUrl.replace(/^https?:\/\//, '')}
            </a>
          )}
        </div>
      )}

      {/*
        * Columns, not a grid. These cards differ wildly in height — the onboarding
        * checklist is eight rows, the certificate card ends in a multi-line shell
        * script — and a two-column grid makes every row as tall as its tallest card.
        * That left a third of the onboarding card empty and pushed everything below
        * it down by the same amount, for nothing.
        *
        * Multi-column flow packs each card against the one above it instead, so a
        * short card costs its own height and no more. `break-inside-avoid` is what
        * keeps a card whole rather than splitting it across the fold, and the margin
        * replaces `gap`, which does not apply between items within a column.
        */}
      <div className="columns-1 xl:columns-2 xl:gap-5 [&>*]:mb-5 [&>*]:break-inside-avoid">
        <Card>
          <Eyebrow>Onboarding</Eyebrow>
          <ul className="mt-3 flex flex-col gap-2">
            {data.onboarding.steps.map((step) => (
              <li key={step.key} className="flex items-center gap-2.5 text-sm">
                <input
                  type="checkbox"
                  aria-label={step.label}
                  checked={step.done}
                  disabled={step.auto || busy}
                  onChange={async (e) => {
                    setBusy(true);
                    try {
                      await api.put(`/platform/tenants/${id}/onboarding`, {
                        key: step.key,
                        done: e.target.checked,
                      });
                      await tenant.reload();
                    } finally {
                      setBusy(false);
                    }
                  }}
                />
                <span style={{ color: step.done ? 'var(--color-text)' : 'var(--color-muted)' }}>
                  {step.label}
                </span>
                {step.auto && (
                  <span className="text-xs" style={{ color: 'var(--color-faint)' }}>
                    tracked automatically
                  </span>
                )}
              </li>
            ))}
          </ul>
          {!data.onboarding.canGoLive && data.status === 'ONBOARDING' && (
            <p className="mt-3 text-xs" style={{ color: 'var(--signal-delayed)' }}>
              Still outstanding: {data.onboarding.outstanding.join(', ')}
            </p>
          )}
        </Card>

        <Card>
          <Eyebrow>TLS certificates</Eyebrow>
          <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
            Certificates are issued per hostname on the proxy host. Point DNS at the server, then run
            this there as root. Running it again is safe — it issues what is missing and leaves valid
            certificates alone, so it is also how you repair a renewal that failed.
          </p>
          <CertificateScript tenantId={id!} />
        </Card>

        {/* What this operator's passengers actually get. Sits with the commercial
            controls, not with the operator's own settings, because it is one (D69). */}
        <ExperienceEditor tenantId={id!} />

        <Card>
          <Eyebrow>Last 30 days</Eyebrow>
          <dl className="mt-3 flex flex-col gap-2 text-sm">
            <div className="flex justify-between">
              <dt style={{ color: 'var(--color-muted)' }}>Bookings</dt>
              <dd className="tabular font-medium">{data.last30Days.bookings}</dd>
            </div>
            <div className="flex justify-between">
              <dt style={{ color: 'var(--color-muted)' }}>Gross booking value</dt>
              <dd className="money font-medium">{money(data.last30Days.gmvCents, data.currency)}</dd>
            </div>
            <div className="flex justify-between">
              <dt style={{ color: 'var(--color-muted)' }}>Our commission</dt>
              <dd className="money font-medium">
                {money(data.last30Days.commissionCents, data.currency)}
              </dd>
            </div>
          </dl>

          <div className="mt-4">
            <Eyebrow>Staff</Eyebrow>
            <ul className="mt-2 flex flex-col gap-1 text-sm">
              {data.users.map((user) => (
                <li key={user.id} className="flex items-center justify-between gap-2">
                  <span>
                    {user.name}
                    <span style={{ color: 'var(--color-faint)' }}> · {user.email}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span style={{ color: 'var(--color-muted)' }}>{user.role}</span>
                    {user.active && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={async () => {
                          // The operator's own managers do this for their people. This is for the
                          // case they cannot: the owner locked out, nobody inside able to help.
                          if (
                            !(await ask({
                              title: `Give ${user.name} a temporary password?`,
                              body: 'This signs them out everywhere. They will need the new password to get back in.',
                              confirmLabel: 'Reset password',
                            }))
                          )
                            return;
                          setBusy(true);
                          setError(null);
                          try {
                            setTemporary({
                              name: user.name,
                              ...(await api.post<{ temporaryPassword: string; expiresAt: string }>(
                                `/platform/tenants/${id}/staff/${user.id}/reset-password`,
                                {},
                              )),
                            });
                          } catch (err) {
                            setError(errorMessage(err) || 'Could not reset that password.');
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        Reset password
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>

            {temporary && (
              <div className="mt-3">
                <Alert tone="warning">
                  <div className="flex flex-col gap-2">
                    <span>
                      Temporary password for <strong>{temporary.name}</strong>, shown once. It expires{' '}
                      {formatDate(temporary.expiresAt, 'UTC')} and can only be used to choose a new one.
                    </span>
                    <code className="select-all rounded px-2 py-1 text-sm" style={{ background: 'rgba(0,0,0,0.25)' }}>
                      {temporary.temporaryPassword}
                    </code>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => void navigator.clipboard?.writeText(temporary.temporaryPassword)}>
                        Copy
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setTemporary(null)}>
                        Done
                      </Button>
                    </div>
                  </div>
                </Alert>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <Eyebrow>Contracts</Eyebrow>
            <Button size="sm" variant="secondary" onClick={() => setNewContract(true)}>
              New version
            </Button>
          </div>
          <p className="mt-1 text-xs" style={{ color: 'var(--color-muted)' }}>
            Append-only: a new version closes the previous one rather than editing it.
          </p>
          <ul className="mt-3 flex flex-col gap-2 text-sm">
            {data.contracts.map((contract) => (
              <li
                key={contract.id}
                className="rounded-lg border p-2.5"
                style={{
                  borderColor:
                    contract.effectiveTo === null ? 'var(--color-primary)' : 'var(--color-line)',
                }}
              >
                <div className="flex justify-between">
                  <span className="font-medium">{contract.type.toLowerCase()}</span>
                  <span className="tabular text-xs" style={{ color: 'var(--color-muted)' }}>
                    {formatDate(contract.effectiveFrom, 'UTC')} –{' '}
                    {contract.effectiveTo ? formatDate(contract.effectiveTo, 'UTC') : 'open'}
                  </span>
                </div>
                <p className="mt-0.5 text-xs" style={{ color: 'var(--color-muted)' }}>
                  {contract.commissionBps !== null &&
                    `${(contract.commissionBps / 100).toFixed(2).replace(/\.?0+$/, '')}% per booking`}
                  {contract.commissionBps !== null && contract.monthlyFeeCents !== null && ' + '}
                  {contract.monthlyFeeCents !== null &&
                    `${money(contract.monthlyFeeCents, contract.currency)} per month`}
                </p>
                {contract.notes && (
                  <p className="mt-1 text-xs" style={{ color: 'var(--color-faint)' }}>
                    {contract.notes}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </Card>

        <Card>
          <Eyebrow>Domains</Eyebrow>
          <ul className="mt-3 flex flex-col gap-2 text-sm">
            {data.domains.map((domain) => (
              <li key={domain.id} className="flex items-center justify-between gap-2">
                <span className="truncate">
                  {domain.domain}
                  <span className="text-xs" style={{ color: 'var(--color-faint)' }}>
                    {' '}· {domain.type.toLowerCase()}
                  </span>
                </span>
                {domain.verifiedAt ? (
                  <span className="text-xs" style={{ color: 'var(--signal-departed)' }}>
                    verified
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={busy}
                    onClick={() => act(`domains/${domain.id}/verify`)}
                  >
                    Mark verified
                  </Button>
                )}
              </li>
            ))}
          </ul>

          <form
            className="mt-3 flex items-end gap-2"
            onSubmit={async (event) => {
              event.preventDefault();
              if (!newDomain.trim()) return;
              await act('domains', { domain: newDomain.trim() });
              setNewDomain('');
            }}
          >
            <Field label="Add a custom domain" htmlFor="d-new">
              <Input
                id="d-new"
                placeholder="booking.example.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="secondary" loading={busy}>
              Add
            </Button>
          </form>
          <p className="mt-2 text-xs" style={{ color: 'var(--color-muted)' }}>
            A custom domain only serves this tenant once it is verified.
          </p>
        </Card>

        <Card className="xl:col-span-2">
          <div className="flex items-center justify-between">
            <Eyebrow>Invoices</Eyebrow>
            <Button
              size="sm"
              variant="secondary"
              loading={busy}
              onClick={() => act('invoices/generate')}
            >
              Generate for last month
            </Button>
          </div>
          {data.invoices.length === 0 ? (
            <p className="mt-2 text-sm" style={{ color: 'var(--color-muted)' }}>
              No invoices yet.
            </p>
          ) : (
            <ul className="mt-3 flex flex-col gap-1.5 text-sm">
              {data.invoices.map((invoice) => (
                <li key={invoice.id} className="flex items-center justify-between">
                  <Link to="/invoices" className="tabular underline-offset-2 hover:underline">
                    {invoice.number}
                  </Link>
                  <span className="flex items-center gap-3">
                    <span className="money">{money(invoice.totalCents, invoice.currency)}</span>
                    <StatusBadge status={invoice.status} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {newContract && (
        <NewContractDrawer
          tenantId={id}
          currency={data.currency}
          currentContract={currentContract}
          onClose={() => setNewContract(false)}
          onCreated={async () => {
            setNewContract(false);
            await tenant.reload();
          }}
        />
      )}
      {dialog}
      {reasonDialog}
    </Page>
  );
}

function NewContractDrawer({
  tenantId,
  currency,
  currentContract,
  onClose,
  onCreated,
}: {
  tenantId: string;
  currency: string;
  currentContract: Contract | undefined;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [form, setForm] = useState({
    type: (currentContract?.type ?? 'COMMISSION') as 'COMMISSION' | 'MONTHLY' | 'HYBRID',
    commissionPercent: currentContract?.commissionBps
      ? String(currentContract.commissionBps / 100)
      : '5',
    monthlyFee: currentContract?.monthlyFeeCents ? String(currentContract.monthlyFeeCents / 100) : '',
    effectiveFrom: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsCommission = form.type === 'COMMISSION' || form.type === 'HYBRID';
  const needsFee = form.type === 'MONTHLY' || form.type === 'HYBRID';

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/platform/tenants/${tenantId}/contracts`, {
        type: form.type,
        ...(needsCommission ? { commissionBps: Math.round(Number(form.commissionPercent) * 100) } : {}),
        ...(needsFee ? { monthlyFeeCents: Math.round(Number(form.monthlyFee) * 100) } : {}),
        effectiveFrom: form.effectiveFrom,
        ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
      });
      await onCreated();
    } catch (err) {
      setError(errorMessage(err, 'Could not create this contract version.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      open
      title="New contract version"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={busy}>
            Create version
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        {currentContract && (
          <Alert tone="info">
            The current contract will be closed on the date you choose. Invoices already issued are
            unaffected.
          </Alert>
        )}
        <Field label="Type" htmlFor="c-type">
          <Select
            id="c-type"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as typeof form.type })}
          >
            <option value="COMMISSION">Commission per booking</option>
            <option value="MONTHLY">Flat monthly fee</option>
            <option value="HYBRID">Both</option>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          {needsCommission && (
            <Field label="Commission (%)" htmlFor="c-comm">
              <Input
                id="c-comm"
                type="number"
                step="0.01"
                min={0}
                max={100}
                className="tabular"
                value={form.commissionPercent}
                onChange={(e) => setForm({ ...form, commissionPercent: e.target.value })}
              />
            </Field>
          )}
          {needsFee && (
            <Field label={`Monthly fee (${currency})`} htmlFor="c-fee">
              <Input
                id="c-fee"
                type="number"
                step="0.01"
                min={0}
                className="tabular"
                value={form.monthlyFee}
                onChange={(e) => setForm({ ...form, monthlyFee: e.target.value })}
              />
            </Field>
          )}
        </div>
        <Field
          label="In effect from"
          htmlFor="c-from"
          hint="A future date is fine — the new terms apply from then."
        >
          <Input
            id="c-from"
            type="date"
            value={form.effectiveFrom}
            onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
          />
        </Field>
        <Field label="Notes" htmlFor="c-notes">
          <Input id="c-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </Field>
      </div>
    </Drawer>
  );
}

/**
 * The provisioning script for one tenant, shown in full and copyable.
 *
 * Fetched on its own rather than folded into the tenant payload: it is a wall of shell that most
 * visits to this page do not need, and the hostnames it names have to be right, so it is generated
 * from the tenant's live domains at the moment it is asked for.
 */
function CertificateScript({ tenantId }: { tenantId: string }) {
  const script = useResource<{
    hostnames: string[];
    script: string;
    offboardScript: string;
    vhostPath: string;
    vhostConfig: string;
  }>(`/platform/tenants/${tenantId}/certificate-script`);
  const [shown, setShown] = useState<'provision' | 'vhost' | 'offboard'>('provision');
  const [copied, setCopied] = useState(false);

  if (script.loading) return <Spinner />;
  if (script.error || !script.data) {
    return <Alert tone="error">{errorMessage(script.error) || 'Could not build the script.'}</Alert>;
  }

  const { hostnames } = script.data;
  const panels = {
    provision: { label: 'Provision', body: script.data.script },
    vhost: { label: 'Commit to Observability', body: script.data.vhostConfig },
    offboard: { label: 'Remove (offboarding)', body: script.data.offboardScript },
  } as const;
  const text = panels[shown].body;

  return (
    <>
      <ul className="mt-3 flex flex-col gap-1 text-xs" style={{ color: 'var(--color-muted)' }}>
        {hostnames.map((host) => (
          <li key={host}>{host}</li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {(Object.keys(panels) as Array<keyof typeof panels>).map((key) => (
          <Button
            key={key}
            variant={key === shown ? undefined : 'ghost'}
            onClick={() => {
              setShown(key);
              setCopied(false);
            }}
          >
            {panels[key].label}
          </Button>
        ))}
        <Button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(text);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              // Clipboard access can be refused (insecure origin, permissions). The text is on
              // screen and selectable, so this is a missing convenience rather than a dead end.
              setCopied(false);
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      {shown === 'vhost' && (
        <p className="mt-3 text-xs" style={{ color: 'var(--color-muted)' }}>
          Commit as <code>{script.data.vhostPath}</code>. A proxy deploy never deletes this file, but
          a rebuilt server has no memory of it — and while it is committed, deleting it only on the
          server brings it back on the next deploy.
        </p>
      )}
      {shown === 'offboard' && (
        <p className="mt-3 text-xs" style={{ color: 'var(--signal-delayed)' }}>
          Removes the vhost, stops renewals and deletes the certificates. Finish by deleting{' '}
          <code>{script.data.vhostPath}</code> from the Observability repo.
        </p>
      )}

      <pre
        className="mt-3 overflow-x-auto rounded p-3 text-[0.6875rem] leading-relaxed"
        style={{ background: 'var(--color-surface-sunken, rgba(0,0,0,0.25))', color: 'var(--color-text)' }}
      >
        {text}
      </pre>
    </>
  );
}
